import { useRef, useState } from 'react';
import { last } from 'lodash';
import { getContext } from '@fiverr-private/fiverr_context';
import { theme } from '@fiverr-private/theme';
import {
    findFirstOverflowedSlideFromLeft,
    findFirstOverflowedSlideFromRight,
    findFirstVisibleSlideFromLeft,
    findFirstVisibleSlideFromRight,
    findSecondVisibleSlideFromTheLeft,
    setCanScrollByPosition,
} from '../../utils';
import type { Direction, ScrollType } from '../../types';
import useScrollCounter from '../useScrollCounter';
import type { UseScrollProps } from './types';

const useScroll = ({
    scrollType,
    isInfinite,
    slides,
    breakpoints,
    withSlidesIndicator = false,
    swipeModeDevice,
    showSwipeIndicator = false,
}: UseScrollProps) => {
    const slidesTrackRef = useRef<HTMLElement>(null);
    const carouselTransformValue = useRef(0);
    const slidesListRef = useRef<HTMLElement>(null);
    const scrolledSlidesStack = useRef<Partial<Record<Direction, number>>[]>([]);

    const [numOfMountedSlides, setNumOfMountedSlides] = useState(0);
    const [canScrollRight, setCanScrollRight] = useState(slides.length > breakpoints.default);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [leftSlideFullyVisible, setLeftSlideFullyVisible] = useState(true);
    const [rightSlideFullyVisible, setRightSlideFullyVisible] = useState(true);

    const isSwipeMode = (): boolean => {
        const { isMobile = false, isTouch = false } = getContext();

        switch (swipeModeDevice) {
            case 'tablet': {
                return isTouch;
            }

            case 'mobile': {
                return isMobile;
            }

            case 'none':
            default: {
                return false;
            }
        }
    };

    const swipeMode = isSwipeMode();

    const { currentPosition, supportSlidesIndicator, resetCounterPosition, changeCounterPosition } = useScrollCounter({
        scrollType,
        slidesToShow: breakpoints.default,
        withSlidesIndicator,
        swipeMode,
        showSwipeIndicator,
    });

    const scrollToPosition = (position: number) => {
        const slides = slidesTrackRef.current?.children || [];

        if (!slides[position] || !shouldScroll()) {
            return;
        }

        const slideRect = slides[position].getBoundingClientRect();

        const slidesListPosition = slidesListRef.current?.getBoundingClientRect();
        const slidesTrackPosition = slidesTrackRef.current?.getBoundingClientRect();

        if (!slidesListPosition || !slidesTrackPosition) {
            return;
        }

        const scrolledAmount = scrollToFirstVisibleSlide({ slideRect, slidesListPosition, slidesTrackPosition });

        if (scrolledAmount) {
            beforeScroll();
            checkTrackPosition();

            if (!swipeMode) {
                setTransformValue();
            } else {
                slidesTrackRef.current?.scrollTo?.({
                    left: slidesTrackRef.current.scrollLeft + scrolledAmount,
                    behavior: 'smooth',
                });
            }

            changeCounterPosition(position);
        }
    };

    /**
     * Called when clicking on the right arrow.
     * First checks if the prev move was left - if so takes the value from the stack.
     * If not calculates how much to move by calling dragSlideByType.
     */
    const scrollRight = () => {
        if (!shouldScroll()) {
            return;
        }

        beforeScroll();

        const { left } = last(scrolledSlidesStack.current) || {};
        const rightEdgeBack = isInfinite && !canScrollRight;

        if (rightEdgeBack) {
            changeCounterPosition(0);
        } else {
            changeCounterPosition(currentPosition + 1);
        }

        if (left) {
            carouselTransformValue.current = carouselTransformValue.current + left;
            scrolledSlidesStack.current.pop();
        } else {
            const slidesListPosition = slidesListRef.current?.getBoundingClientRect();
            const slidesTrackPosition = slidesTrackRef.current?.getBoundingClientRect();
            const slides = slidesTrackRef.current?.children || [];

            if (slidesListPosition && slidesTrackPosition) {
                if (rightEdgeBack) {
                    const firstSlide = slides[0].getBoundingClientRect();
                    scrollToLastVisibleSlide({
                        slideRect: firstSlide,
                        slidesListPosition,
                        slidesTrackPosition,
                        direction: 'right',
                    });
                } else {
                    dragSlideByType[scrollType]({
                        slidesListPosition,
                        slidesTrackPosition,
                        slides,
                        direction: 'right',
                    });
                }
            }
        }

        checkTrackPosition();
        setTransformValue();
    };

    /**
     * Called when clicking on the left arrow.
     * First checks if the prev move was right - if so takes the value from the stack.
     * If not calculates how much to move by calling dragSlideByType.
     */
    const scrollLeft = () => {
        if (!shouldScroll()) {
            return;
        }

        beforeScroll();

        const { right } = last(scrolledSlidesStack.current) || {};
        const leftEdgeBack = isInfinite && !canScrollLeft;

        if (leftEdgeBack) {
            changeCounterPosition(slides.length - 1);
        } else {
            changeCounterPosition(currentPosition - 1);
        }

        if (right) {
            carouselTransformValue.current = carouselTransformValue.current + right;
            scrolledSlidesStack.current.pop();
        } else {
            const slidesListPosition = slidesListRef.current?.getBoundingClientRect();
            const slidesTrackPosition = slidesTrackRef.current?.getBoundingClientRect();
            const slides = slidesTrackRef.current?.children || [];

            if (slidesListPosition && slidesTrackPosition) {
                if (leftEdgeBack) {
                    const lastSlide = slides[slides.length - 1].getBoundingClientRect();
                    scrollToLastVisibleSlide({
                        slideRect: lastSlide,
                        slidesListPosition,
                        slidesTrackPosition,
                        direction: 'left',
                    });
                } else {
                    dragSlideByType[scrollType]({
                        slidesListPosition,
                        slidesTrackPosition,
                        slides,
                        direction: 'left',
                    });
                }
            }
        }

        checkTrackPosition();
        setTransformValue();
    };

    /**
     * Scroll type mapper
     * handleSingleScroll is called when the scrollType is single.
     * handleRowScroll is called when the scrollType is row.
     */
    const dragSlideByType: Record<ScrollType, any> = {
        single: ({ slidesListPosition, slidesTrackPosition, slides, direction }) => {
            handleSingleScroll({ slidesListPosition, slidesTrackPosition, slides })[direction]();
        },
        row: ({ slidesListPosition, slidesTrackPosition, slides, direction }) => {
            handleRowScroll({ slidesListPosition, slidesTrackPosition, slides })[direction]();
        },
    };

    /**
     * Set new carouselTransformValue and update the scrolledSlidesStack.
     */
    const moveSlide = ({ amountToMove, direction }: { amountToMove: number; direction?: Direction }) => {
        carouselTransformValue.current = carouselTransformValue.current - amountToMove;

        if (direction) {
            scrolledSlidesStack.current.push({ [direction]: amountToMove });
        }
    };

    /**
     * Change the carousel transform value by dragging a slide to the most left point of the carousel container.
     */
    const scrollToFirstVisibleSlide = ({
        slideRect,
        slidesListPosition,
        slidesTrackPosition,
        direction,
    }: {
        slideRect: DOMRect;
        slidesListPosition: DOMRect;
        slidesTrackPosition: DOMRect;
        direction?: Direction;
    }) => {
        if (!slidesTrackRef.current) {
            return;
        }

        let amountToMove = slideRect.left - slidesListPosition.left;

        const isLastScroll =
            slidesTrackRef.current.scrollWidth +
                carouselTransformValue.current -
                amountToMove -
                slidesTrackPosition.width <
            1;

        if (isLastScroll) {
            amountToMove =
                carouselTransformValue.current + (slidesTrackRef.current.scrollWidth - slidesTrackPosition.width);
        }

        moveSlide({ amountToMove, direction });

        return amountToMove;
    };

    /**
     * Change the carousel transform value by dragging a slide to the most right point of the carousel container.
     */
    const scrollToLastVisibleSlide = ({
        slideRect,
        slidesListPosition,
        slidesTrackPosition,
        direction,
    }: {
        slideRect: DOMRect;
        slidesListPosition: DOMRect;
        slidesTrackPosition: DOMRect;
        direction?: Direction;
    }) => {
        let amountToMove = slideRect.right - slidesListPosition.right;

        const isLastScroll = slidesTrackPosition.left - amountToMove >= 0;

        if (isLastScroll) {
            amountToMove = carouselTransformValue.current;
        }

        moveSlide({ amountToMove, direction });
    };

    /**
     * Move one slide.
     */
    const handleSingleScroll = ({
        slidesListPosition,
        slidesTrackPosition,
        slides,
    }: {
        slidesListPosition: DOMRect;
        slidesTrackPosition: DOMRect;
        slides: NodeListOf<HTMLElement>;
    }): Record<Direction, any> => ({
        left: () => {
            const overflowedFromLeft = findFirstOverflowedSlideFromLeft(slidesListPosition, slides);

            if (overflowedFromLeft) {
                scrollToFirstVisibleSlide({
                    slideRect: overflowedFromLeft,
                    slidesListPosition,
                    slidesTrackPosition,
                    direction: 'left',
                });
            }
        },
        right: () => {
            const secondVisibleSlide = findSecondVisibleSlideFromTheLeft(slidesListPosition, slides);

            if (secondVisibleSlide) {
                scrollToFirstVisibleSlide({
                    slideRect: secondVisibleSlide,
                    slidesListPosition,
                    slidesTrackPosition,
                    direction: 'right',
                });
            }
        },
    });

    /**
     * Move full row - the amount of visible slides.
     */
    const handleRowScroll = ({
        slidesListPosition,
        slidesTrackPosition,
        slides,
    }: {
        slidesListPosition: DOMRect;
        slidesTrackPosition: DOMRect;
        slides: NodeListOf<HTMLElement>;
    }): Record<Direction, any> => ({
        left: () => {
            const overflowedFromLeft = findFirstOverflowedSlideFromLeft(slidesListPosition, slides);

            if (overflowedFromLeft) {
                scrollToLastVisibleSlide({
                    slideRect: overflowedFromLeft,
                    slidesListPosition,
                    slidesTrackPosition,
                    direction: 'left',
                });
            }
        },
        right: () => {
            const overflowedFromRight = findFirstOverflowedSlideFromRight(slidesListPosition, slides);

            if (overflowedFromRight) {
                scrollToFirstVisibleSlide({
                    slideRect: overflowedFromRight,
                    slidesListPosition,
                    slidesTrackPosition,
                    direction: 'right',
                });
            }
        },
    });

    /**
     * Check if able to scroll
     * If isAnimating is true abort scroll.
     */
    const shouldScroll = (): boolean => !isAnimating;

    /**
     * Called before scrolling
     * Set isAnimating to true and add transition attribute.
     */
    const beforeScroll = () => {
        setIsAnimating(true);
        addTransition();
    };

    /**
     * Adding the transition before scrolling.
     */
    const addTransition = () => {
        if (!slidesTrackRef.current) {
            return;
        }

        slidesTrackRef.current.style.transition = `transform ${theme.numericAnimationDurations.slow3}ms ease`;
    };

    /**
     * Remove the transition after scrolling.
     */
    const onTransitionEnd = ({ target }) => {
        if (target === slidesTrackRef.current && slidesTrackRef.current) {
            slidesTrackRef.current.style.removeProperty('transition');
            setIsAnimating(false);
            checkEdgesFullyVisible();
        }
    };

    /**
     * Setting transform value to the carousel so it will move.
     */
    const setTransformValue = () => {
        if (!slidesTrackRef.current) {
            return;
        }

        slidesTrackRef.current.style.transform = `translate3d(${carouselTransformValue.current}px, 0, 0)`;
    };

    /**
     * Check the track position to know if there are more slides on either left or right point.
     */
    const checkTrackPosition = () => {
        if (!slidesTrackRef.current) {
            return;
        }

        const trackRightPosition = slidesTrackRef.current.offsetWidth - slidesTrackRef.current.scrollWidth;

        const isTrackOnRightPosition = Math.floor(carouselTransformValue.current) <= trackRightPosition;
        const isTrackOnLeftPosition = carouselTransformValue.current === 0;

        setCanScrollByPosition(isTrackOnLeftPosition, setCanScrollLeft);
        setCanScrollByPosition(isTrackOnRightPosition, setCanScrollRight);
    };

    /**
     * Check if the most left and right visible slides are fully visible
     */
    const checkEdgesFullyVisible = () => {
        const slidesListPosition = slidesListRef.current?.getBoundingClientRect() || ({} as DOMRect);
        const slides = slidesTrackRef.current?.children || [];

        const leftVisibleSlide = findFirstVisibleSlideFromLeft(slidesListPosition, slides);
        const rightVisibleSlide = findFirstVisibleSlideFromRight(slidesListPosition, slides);

        if (leftVisibleSlide) {
            setLeftSlideFullyVisible(
                Math.floor(leftVisibleSlide.getBoundingClientRect().left) >= Math.floor(slidesListPosition.left)
            );
        }

        if (rightVisibleSlide) {
            setRightSlideFullyVisible(
                Math.floor(rightVisibleSlide.getBoundingClientRect().right) <= Math.floor(slidesListPosition.right)
            );
        }
    };

    /**
     * Reset carousel props to default.
     */
    const resetCarousel = () => {
        scrolledSlidesStack.current = [];
        carouselTransformValue.current = 0;
        setTransformValue();
        slidesTrackRef.current?.scrollTo?.({ left: 0 });
        setCanScrollRight(slides.length > breakpoints.default);
        setCanScrollLeft(false);
        checkTrackPosition();
        resetCounterPosition();
        checkEdgesFullyVisible();
    };

    /**
     * Update the Counter Position when user scroll the carousel by swipe instead of by clicking on the arrows
     * (it's important to keep the Counter Position up to date in order to update the dots' indicator)
     * */
    const updateCounterPositionOnScrollBySwipe = ({ target: listContainer }: { target: HTMLElement }) => {
        const newPosition = searchItemInContainer(listContainer, currentPosition);
        if (newPosition !== currentPosition) {
            changeCounterPosition(newPosition);
        }
    };

    const searchItemInContainer = (listContainer: HTMLElement, index: number) => {
        const boundary = listContainer.getBoundingClientRect();
        const { left, right } = listContainer.children[index].getBoundingClientRect();
        const swipeToLeft = left > boundary.right;
        const swipeToRight = right < boundary.left;

        if (swipeToLeft) {
            const nextIndex = index - 1;
            if (nextIndex >= 0) {
                return searchItemInContainer(listContainer, nextIndex);
            }
        }
        if (swipeToRight) {
            const nextIndex = index + 1;
            if (nextIndex < listContainer.childElementCount) {
                return searchItemInContainer(listContainer, nextIndex);
            }
        }

        return index;
    };

    const canScrollOnInfinite = !!(isInfinite && (canScrollLeft || canScrollRight));

    return {
        scrollRight,
        scrollLeft,
        canScrollOnInfinite,
        scrollToPosition,
        onTransitionEnd,
        resetCarousel,
        scrollToFirstVisibleSlide,
        scrollToLastVisibleSlide,
        setTransformValue,
        checkTrackPosition,
        updateCounterPositionOnScrollBySwipe,
        carouselTransformValue,
        scrolledSlidesStack,
        slidesTrackRef,
        canScrollLeft,
        slidesListRef,
        canScrollRight,
        currentPosition,
        supportSlidesIndicator,
        breakpoints,
        swipeMode,
        slides,
        leftSlideFullyVisible,
        rightSlideFullyVisible,
        numOfMountedSlides,
        setNumOfMountedSlides,
    };
};

export default useScroll;
